/**
 * Persists the user's last v2 new-presentation form selections to
 * localStorage so the form can pre-fill them on the next visit.
 *
 * Excludes the topic (per product decision) and the custom-persona draft
 * (which would carry base64 image/audio blobs). All fields are optional
 * so partial / future-incompatible data degrades gracefully without a
 * schema version.
 *
 * Validation is per-field tolerant: an entry with a wrong-typed field
 * (e.g. hand-edited `numberOfSlides: "ten"`) is read as if that field
 * were absent, leaving valid sibling fields restored. A stored
 * `numberOfSlides` outside the allowed slide-count stops is snapped to
 * the nearest stop (ties round up). The form still validates string
 * ids against the live $v2Options at the call site, because the module
 * cannot see backend options.
 */

import { z } from "zod";
import { nearestSlideCountStop } from "../lib/generate/slideCountStops.ts";

export const REMEMBERED_SETTINGS_STORAGE_KEY =
	"ai-new-presentation-v2-settings";

const rememberedSettingsSchema = z.object({
	numberOfSlides: z
		.number()
		.finite()
		.transform(nearestSlideCountStop)
		.optional()
		.catch(undefined),
	selectedPersonaId: z.string().optional().catch(undefined),
	selectedImageGenerator: z.string().optional().catch(undefined),
	selectedSpeakerVideoGenerator: z.string().optional().catch(undefined),
	selectedTextGenerator: z.string().optional().catch(undefined),
});

export type RememberedNewPresentationV2Settings = z.output<
	typeof rememberedSettingsSchema
>;

// When saving, callers supply raw values (e.g. an arbitrary `numberOfSlides:
// number` from the form variable); the schema's `.transform` only narrows on
// the read path. `z.input` matches the un-transformed shape so the form
// doesn't need to pre-narrow its bound state to `SlideCountStop`.
type RememberedNewPresentationV2SettingsInput = z.input<
	typeof rememberedSettingsSchema
>;

export function loadRememberedSettings(): RememberedNewPresentationV2Settings {
	const raw = localStorage.getItem(REMEMBERED_SETTINGS_STORAGE_KEY);
	if (raw === null) {
		return {};
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch (error) {
		console.warn("remember-settings: failed to parse stored JSON", error);
		return {};
	}
	const result = rememberedSettingsSchema.safeParse(parsed);
	if (!result.success) {
		console.warn(
			"remember-settings: stored data did not match schema",
			result.error,
		);
		return {};
	}
	return result.data;
}

export function saveRememberedSettings(
	settings: RememberedNewPresentationV2SettingsInput,
): void {
	try {
		localStorage.setItem(
			REMEMBERED_SETTINGS_STORAGE_KEY,
			JSON.stringify(settings),
		);
	} catch (error) {
		// Quota exceeded, storage disabled in private mode, etc. — must
		// never block the submit flow.
		console.warn("remember-settings: failed to save", error);
	}
}
